// Pure retail sentiment interpretation, shared by the public page and XAU MacroLens.
// Contrarian logic: a crowded retail side is liquidity for the opposite move.

export type ContrarianBias = "bullish" | "mild_bullish" | "neutral" | "mild_bearish" | "bearish";

export type SentimentReading = {
  bias: ContrarianBias
  label: string
  score: number // -2 (bearish) to +2 (bullish) for MacroLens aggregation
  crowdedSide: "long" | "short" | "balanced"
  liquidityPool: "SSL" | "BSL" | null
  ratio: number // crowded side divided by the other side
}

export function classifySentiment(longPct: number): SentimentReading {
  const shortPct = 100 - longPct
  const ratio = longPct >= shortPct ? longPct / Math.max(shortPct, 0.01) : shortPct / Math.max(longPct, 0.01)
  const r = Math.round(ratio * 100) / 100
  if (longPct >= 70) return { bias: "bearish", label: "Bearish (crowd heavily long)", score: -2, crowdedSide: "long", liquidityPool: "SSL", ratio: r }
  if (longPct > 55) return { bias: "mild_bearish", label: "Mildly bearish", score: -1, crowdedSide: "long", liquidityPool: "SSL", ratio: r }
  if (longPct <= 30) return { bias: "bullish", label: "Bullish (crowd heavily short)", score: 2, crowdedSide: "short", liquidityPool: "BSL", ratio: r }
  if (longPct < 45) return { bias: "mild_bullish", label: "Mildly bullish", score: 1, crowdedSide: "short", liquidityPool: "BSL", ratio: r }
  return { bias: "neutral", label: "Neutral (balanced crowd)", score: 0, crowdedSide: "balanced", liquidityPool: null, ratio: r }
}

/** Parse the Myfxbook community outlook markdown for XAUUSD. */
export function parseMyfxbookOutlook(md: string) {
  const sent = md.match(/(\d+(?:\.\d+)?)% of the forex traders are currently going short[^.]*?average price of (\d+(?:\.\d+)?)[^%]*?(\d+(?:\.\d+)?)% of the forex traders are going long[^.]*?average price of (\d+(?:\.\d+)?)/i)
  if (!sent) return null
  const shortPct = Number(sent[1])
  const longPct = Number(sent[3])
  if (!Number.isFinite(longPct) || !Number.isFinite(shortPct) || Math.abs(longPct + shortPct - 100) > 2) return null
  // Sentence-final periods must never reach Number(), which would store null.
  const price = (s: string | undefined) => { const x = Number(s); return Number.isFinite(x) && x > 0 ? x : null }
  const num = (s: string | undefined) => (s ? Number(s.replace(/,/g, "")) : null)
  const shortRow = md.match(/\|\s*Short\s*\|[^|]*\|\s*([\d,.]+)\s*lots\s*\|\s*([\d,]+)\s*\|/i)
  const longRow = md.match(/\|\s*Long\s*\|[^|]*\|\s*([\d,.]+)\s*lots\s*\|\s*([\d,]+)\s*\|/i)
  return {
    longPct,
    shortPct,
    shortAvg: price(sent[2]),
    longAvg: price(sent[4]),
    shortLots: num(shortRow?.[1]),
    shortPositions: num(shortRow?.[2]),
    longLots: num(longRow?.[1]),
    longPositions: num(longRow?.[2]),
  }
}

export type TrappedSide = {
  side: "long" | "short"
  avgEntry: number
  distanceUsd: number // positive = in profit, negative = underwater
  underwater: boolean
  avgLotsPerPosition: number | null
}

/** Where each retail side stands versus spot. Underwater sides hold the stops price is most likely to raid. */
export function trappedAnalysis(spot: number, s: { longAvg: number | null; shortAvg: number | null; longLots: number | null; shortLots: number | null; longPositions: number | null; shortPositions: number | null }): TrappedSide[] {
  const out: TrappedSide[] = []
  const per = (l: number | null, p: number | null) => (l != null && p ? Math.round((l / p) * 1000) / 1000 : null)
  if (s.longAvg != null) {
    const d = Math.round((spot - s.longAvg) * 100) / 100
    out.push({ side: "long", avgEntry: s.longAvg, distanceUsd: d, underwater: d < 0, avgLotsPerPosition: per(s.longLots, s.longPositions) })
  }
  if (s.shortAvg != null) {
    const d = Math.round((s.shortAvg - spot) * 100) / 100
    out.push({ side: "short", avgEntry: s.shortAvg, distanceUsd: d, underwater: d < 0, avgLotsPerPosition: per(s.shortLots, s.shortPositions) })
  }
  return out
}
